import React, { useEffect, useMemo, useState } from 'react';
import { Search, MapPin, Loader2, AlertTriangle, CheckCircle2, X, Star } from 'lucide-react';
import { adminApi } from '../../api';

/**
 * Admin: Cerca e importa attività (Google Places Text Search).
 * - Text Search (non Nearby) — funziona anche per sottocategorie fuori tassonomia.
 * - Marca duplicati (place_id o nome+indirizzo simili) ma non li nasconde.
 * - Import in stato `draft`; l'arricchimento gira in background.
 * - Non tocca mai attività esistenti.
 */
export default function GoogleDiscoverImport({ cities, categories, subcategories, onImported }) {
  const [open, setOpen] = useState(false);
  const [citySlug, setCitySlug] = useState('');
  const [categoryKey, setCategoryKey] = useState('');
  const [subcategoryKey, setSubcategoryKey] = useState('');
  const [freeText, setFreeText] = useState('');
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [results, setResults] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [selected, setSelected] = useState({}); // place_id -> true
  const [lastQuery, setLastQuery] = useState('');
  const [importing, setImporting] = useState(false);
  const [importJob, setImportJob] = useState(null);
  const [notice, setNotice] = useState(null);

  const subsForCat = useMemo(
    () => (subcategories || []).filter(s => s.category_key === categoryKey),
    [subcategories, categoryKey]
  );

  const openModal = () => {
    setCitySlug((cities || [])[0]?.slug || '');
    setCategoryKey((categories || [])[0]?.key || '');
    setSubcategoryKey('');
    setFreeText('');
    setResults([]);
    setSelected({});
    setLastQuery('');
    setNotice(null);
    setImportJob(null);
    setOpen(true);
  };

  const close = () => {
    if (importing || searching) return;
    setOpen(false);
  };

  const doSearch = async () => {
    if (!citySlug || !categoryKey) {
      setNotice({ kind: 'error', text: 'Seleziona città e categoria' });
      return;
    }
    setSearching(true);
    setNotice(null);
    setResults([]);
    setSelected({});
    setNextPageToken(null);
    try {
      const r = await adminApi.googleDiscover({
        city_slug: citySlug,
        category_key: categoryKey,
        subcategory: subcategoryKey || null,
        query: freeText || null,
      });
      setResults(r.results || []);
      setNextPageToken(r.next_page_token || null);
      setLastQuery(r.query || '');
      if (!(r.results || []).length) {
        setNotice({ kind: 'info', text: 'Nessun risultato — prova ad affinare la query.' });
      }
    } catch (e) {
      setNotice({ kind: 'error', text: e.response?.data?.detail || e.message });
    }
    setSearching(false);
  };

  // Fetch the next page of Google Places results using the token returned by
  // the previous call. Google allows up to 3 pages (~60 results total). We
  // append to the existing list and re-sort locally so the highest-rated
  // result across all pages stays on top.
  const loadMore = async () => {
    if (!nextPageToken || loadingMore) return;
    setLoadingMore(true);
    setNotice(null);
    try {
      const r = await adminApi.googleDiscover({
        city_slug: citySlug,
        category_key: categoryKey,
        subcategory: subcategoryKey || null,
        query: freeText || null,
        page_token: nextPageToken,
      });
      const newOnes = r.results || [];
      // Merge + dedupe by place_id (defensive; Google shouldn't repeat but…)
      setResults(prev => {
        const seen = new Set(prev.map(x => x.place_id));
        const merged = [...prev, ...newOnes.filter(x => !seen.has(x.place_id))];
        // Re-sort by rating desc, review_count desc so cross-page top stays first
        merged.sort((a, b) => {
          const ra = a.rating ?? -1, rb = b.rating ?? -1;
          if (rb !== ra) return rb - ra;
          const ca = a.review_count ?? -1, cb = b.review_count ?? -1;
          return cb - ca;
        });
        return merged;
      });
      setNextPageToken(r.next_page_token || null);
    } catch (e) {
      setNotice({ kind: 'error', text: e.response?.data?.detail || e.message });
    }
    setLoadingMore(false);
  };

  const toggle = (placeId) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[placeId]) delete next[placeId]; else next[placeId] = true;
      return next;
    });
  };

  const selectedCount = Object.keys(selected).length;

  const doImport = async () => {
    const items = results.filter(r => selected[r.place_id]).map(r => ({
      place_id: r.place_id,
      name: r.name,
      address: r.address,
    }));
    if (!items.length) return;
    setImporting(true);
    setNotice(null);
    try {
      const r = await adminApi.googleBulkImport({
        city_slug: citySlug,
        category_key: categoryKey,
        subcategory: subcategoryKey || null,
        items,
      });
      const createdCount = (r.created || []).length;
      const skippedCount = (r.skipped || []).length;
      setNotice({
        kind: 'success',
        text: `${createdCount} attività importate come bozza${skippedCount ? ` · ${skippedCount} saltate (già collegate)` : ''}. Arricchimento Google in corso in background.`,
      });
      // Remove imported from results so user isn't tempted to re-import
      const importedIds = new Set((r.created || []).map(c => c.place_id));
      setResults(prev => prev.map(row => importedIds.has(row.place_id) ? { ...row, already_exists: true, existing_name: row.name, just_imported: true } : row));
      setSelected({});
      if (r.enrich_job_id) {
        setImportJob({ id: r.enrich_job_id, status: 'running', processed: 0, total: createdCount });
        pollEnrich(r.enrich_job_id);
      }
      if (onImported) onImported();
    } catch (e) {
      setNotice({ kind: 'error', text: e.response?.data?.detail || e.message });
    }
    setImporting(false);
  };

  const pollEnrich = async (jobId) => {
    try {
      const j = await adminApi.googleBulkStatus(jobId);
      setImportJob({ ...j, id: jobId });
      if (j.status === 'running') setTimeout(() => pollEnrich(jobId), 1500);
      else if (onImported) onImported();
    } catch (e) { /* silent */ }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="google-discover-btn"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-sm font-medium"
      >
        <Search size={14}/> Cerca e importa da Google
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={close}>
          <div
            className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-xl bg-white border border-emerald-200 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2 font-bold text-slate-800">
                <Search size={18} className="text-emerald-600"/> Cerca e importa attività da Google
              </div>
              <button onClick={close} className="text-slate-400 hover:text-slate-700" aria-label="Chiudi" data-testid="discover-close-btn">
                <X size={20}/>
              </button>
            </div>

            {/* Form */}
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700">Città</span>
                  <select
                    value={citySlug}
                    onChange={e => setCitySlug(e.target.value)}
                    data-testid="discover-city-select"
                    className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300 focus:border-emerald-500 focus:ring focus:ring-emerald-100 focus:outline-none bg-white"
                  >
                    <option value="">— Seleziona città —</option>
                    {(cities || []).filter(c => c.status !== 'coming_soon').map(c => (
                      <option key={c.slug} value={c.slug}>{c.name || c.slug}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700">Categoria</span>
                  <select
                    value={categoryKey}
                    onChange={e => { setCategoryKey(e.target.value); setSubcategoryKey(''); }}
                    data-testid="discover-cat-select"
                    className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300 focus:border-emerald-500 focus:ring focus:ring-emerald-100 focus:outline-none bg-white"
                  >
                    <option value="">— Seleziona categoria —</option>
                    {(categories || []).map(c => (
                      <option key={c.key} value={c.key}>{(c.label && (c.label.it || c.label.en)) || c.key}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700">Sottocategoria <span className="text-slate-400">(opzionale)</span></span>
                  <select
                    value={subcategoryKey}
                    onChange={e => setSubcategoryKey(e.target.value)}
                    disabled={!subsForCat.length}
                    data-testid="discover-sub-select"
                    className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300 focus:border-emerald-500 focus:ring focus:ring-emerald-100 focus:outline-none bg-white disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    <option value="">— Nessuna (usa solo la categoria) —</option>
                    {subsForCat.map(s => (
                      <option key={s.key} value={s.key}>{(s.label && (s.label.it || s.label.en)) || s.key}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700">Testo libero <span className="text-slate-400">(opzionale)</span></span>
                  <input
                    type="text"
                    value={freeText}
                    onChange={e => setFreeText(e.target.value)}
                    placeholder='es. "pizzeria gourmet", "vista lago"'
                    data-testid="discover-freetext-input"
                    className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300 focus:border-emerald-500 focus:ring focus:ring-emerald-100 focus:outline-none"
                  />
                </label>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="text-[11px] text-slate-500">
                  Query costruita: <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200">{buildPreviewQuery(categories, subcategories, categoryKey, subcategoryKey, cities, citySlug, freeText)}</code>
                </div>
                <button
                  type="button"
                  onClick={doSearch}
                  disabled={searching || !citySlug || !categoryKey}
                  data-testid="discover-search-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-semibold disabled:opacity-60"
                >
                  {searching ? <Loader2 size={14} className="animate-spin"/> : <Search size={14}/>} Cerca
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {notice && (
                <div
                  className={`mb-3 px-3 py-2 rounded-md text-sm border ${
                    notice.kind === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
                    notice.kind === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                    'bg-slate-50 border-slate-200 text-slate-700'
                  }`}
                  data-testid="discover-notice"
                >
                  {notice.text}
                </div>
              )}

              {importJob && (
                <div className="mb-3 p-3 rounded-md border border-blue-200 bg-blue-50" data-testid="import-enrich-progress">
                  <div className="text-xs font-semibold text-blue-800 mb-1">
                    Arricchimento Google in corso — {importJob.processed} / {importJob.total}
                    {importJob.status !== 'running' && ' · ✅ completato'}
                  </div>
                  <div className="w-full h-2 bg-white rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${importJob.total ? Math.round((importJob.processed / importJob.total) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              )}

              {!results.length && !searching && (
                <div className="text-center text-slate-500 text-sm py-10">
                  Compila i campi e clicca <strong>Cerca</strong> per vedere i suggerimenti di Google.
                </div>
              )}

              {results.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs text-slate-500 mb-2">
                    {results.length} risultati trovati per <span className="font-mono">{lastQuery}</span>
                  </div>
                  {results.map(r => {
                    const dupe = r.already_exists;
                    const disabled = false; // sempre lasciamo la scelta all'admin
                    return (
                      <label
                        key={r.place_id}
                        data-testid={`discover-result-${r.place_id}`}
                        className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${
                          selected[r.place_id]
                            ? 'border-emerald-500 bg-emerald-50/60'
                            : dupe
                              ? 'border-amber-300 bg-amber-50/50 hover:bg-amber-50'
                              : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!!selected[r.place_id]}
                          onChange={() => toggle(r.place_id)}
                          disabled={disabled}
                          data-testid={`discover-check-${r.place_id}`}
                          className="mt-1 h-4 w-4 accent-emerald-600"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-800 truncate">{r.name || '—'}</span>
                            {dupe && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                                <AlertTriangle size={10}/> {r.just_imported ? 'Appena importata' : 'Probabile duplicato'}
                              </span>
                            )}
                            {typeof r.rating === 'number' && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-800 border border-yellow-200">
                                <Star size={10} className="fill-yellow-500 text-yellow-500"/> {r.rating.toFixed(1)}{r.review_count ? ` · ${r.review_count}` : ''}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
                            <MapPin size={11} className="text-slate-400"/> {r.address || '—'}
                          </div>
                          {dupe && r.existing_name && !r.just_imported && (
                            <div className="text-[11px] text-amber-700 mt-1">
                              Già presente come <strong>{r.existing_name}</strong>{r.existing_city ? ` (${r.existing_city})` : ''} — importa comunque a tuo rischio.
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}

                  {nextPageToken && (
                    <div className="flex justify-center pt-2">
                      <button
                        type="button"
                        onClick={loadMore}
                        disabled={loadingMore}
                        data-testid="discover-load-more-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-700 font-semibold disabled:opacity-60"
                      >
                        {loadingMore ? (
                          <>
                            <Loader2 size={14} className="animate-spin"/> Carico altri risultati…
                          </>
                        ) : (
                          <>Carica altri risultati (fino a 20 in più)</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                {selectedCount > 0 ? (
                  <span>
                    <CheckCircle2 size={12} className="inline text-emerald-600 mr-1"/>
                    {selectedCount} selezionate — verranno importate come <strong>bozza</strong>.
                  </span>
                ) : (
                  <span>Le attività importate restano in <strong>bozza</strong> finché non le pubblichi.</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={close}
                  disabled={importing}
                  className="px-3 py-2 text-sm rounded-md border border-slate-300 hover:bg-slate-50 text-slate-700 disabled:opacity-60"
                >
                  Chiudi
                </button>
                <button
                  type="button"
                  onClick={doImport}
                  disabled={importing || selectedCount === 0}
                  data-testid="discover-import-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 font-semibold disabled:opacity-60"
                >
                  {importing ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
                  Importa selezionate{selectedCount ? ` (${selectedCount})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function buildPreviewQuery(categories, subcategories, categoryKey, subcategoryKey, cities, citySlug, freeText) {
  const seed = (() => {
    if (subcategoryKey) {
      const s = (subcategories || []).find(x => x.category_key === categoryKey && x.key === subcategoryKey);
      if (s) return (s.label && (s.label.it || s.label.en)) || s.key;
    }
    const c = (categories || []).find(x => x.key === categoryKey);
    if (c) return (c.label && (c.label.it || c.label.en)) || c.key;
    return categoryKey || '…';
  })();
  const city = (cities || []).find(x => x.slug === citySlug);
  const cityLabel = city ? (city.name || city.slug) : (citySlug || '…');
  const extra = (freeText || '').trim();
  return `${seed}${extra ? ' ' + extra : ''} a ${cityLabel}`;
}
